import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Check, Trash2, AlertTriangle, Lock, Shield, Bell } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date to readable format
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'JUST NOW';
  if (diffMins < 60) return `${diffMins}M AGO`;
  if (diffHours < 24) return `${diffHours}H AGO`;
  if (diffDays < 7) return `${diffDays}D AGO`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Get icon for notification type
const getNotificationIcon = (type) => {
  switch (type) {
    case 'account_locked':
      return <Lock className="w-4 h-4 text-red-400" />;
    case 'security_alert':
      return <AlertTriangle className="w-4 h-4 text-amber-400" />;
    default:
      return <Bell className="w-4 h-4 text-zinc-400" />;
  }
};

export default function AdminNotifications() {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const getAuthHeader = useCallback(() => {
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/notifications`, {
        headers: getAuthHeader()
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  // Fetch on mount and when popover opens
  useEffect(() => {
    if (token) {
      fetchNotifications();
    }
  }, [token, fetchNotifications]);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (!token) return;
    
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/admin/notifications/${notificationId}/read`, {}, {
        headers: getAuthHeader()
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/admin/notifications/read-all`, {}, {
        headers: getAuthHeader()
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/admin/notifications/${notificationId}`, {
        headers: getAuthHeader()
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      // Update unread count if deleted was unread
      const deleted = notifications.find(n => n.id === notificationId);
      if (deleted && !deleted.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const clearAll = async () => {
    try {
      await axios.delete(`${API}/admin/notifications`, {
        headers: getAuthHeader()
      });
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button 
          className="relative p-2 text-tiffany-500 transition-colors"
          data-testid="admin-notifications-btn"
        >
          <Shield className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-mono rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-[calc(100vw-32px)] sm:w-80 max-w-80 p-0 bg-zinc-900 border-zinc-700 rounded-none"
        align="end"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-zinc-800">
          <span className="font-mono text-xs text-white">
            NOTIFICATIONS {unreadCount > 0 && `(${unreadCount})`}
          </span>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="h-6 px-2 text-[10px] text-zinc-400 hover:text-tiffany-500"
              >
                <Check className="w-3 h-3 mr-1" />
                READ ALL
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="h-6 px-2 text-[10px] text-zinc-400 hover:text-red-400"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
        
        {/* Notifications List */}
        <div className="max-h-80 overflow-y-auto">
          {loading && notifications.length === 0 ? (
            <div className="p-6 text-center">
              <span className="font-mono text-xs text-zinc-500">LOADING...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="w-6 h-6 text-zinc-700 mx-auto mb-2" />
              <span className="font-mono text-xs text-zinc-500">NO NOTIFICATIONS</span>
            </div>
          ) : (
            notifications.map((notification) => (
              <div 
                key={notification.id}
                className={`p-3 border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors ${
                  !notification.read ? 'bg-zinc-800/30' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`font-mono text-xs ${!notification.read ? 'text-white' : 'text-zinc-400'}`}>
                        {notification.title}
                      </span>
                      <span className="font-mono text-[10px] text-zinc-600 flex-shrink-0">
                        {formatDate(notification.created_at)}
                      </span>
                    </div>
                    
                    <p className="font-mono text-[10px] text-zinc-500 mt-1 break-words uppercase">
                      {notification.message}
                    </p>
                    
                    {/* Details for account_locked */}
                    {notification.type === 'account_locked' && notification.details && (
                      <div className="mt-2 p-2 bg-zinc-900 border border-zinc-700 text-[10px] font-mono">
                        <div className="flex justify-between text-zinc-500">
                          <span>IP:</span>
                          <span className="text-zinc-300">{notification.details.ip_address}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-2">
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="font-mono text-[10px] text-tiffany-500 hover:text-tiffany-400"
                        >
                          MARK READ
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="font-mono text-[10px] text-zinc-500 hover:text-red-400"
                      >
                        DELETE
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
